"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type {
  SiteFooterSocialItem,
  SiteFooterQrSocialItem,
} from "@/shared/i18n/site-copy";
import { FireflyIcon } from "@/shared/ui/firefly-icon";
import { cn } from "@/shared/utils/cn";

type FooterSocialLinksProps = {
  items: SiteFooterSocialItem[];
  variant?: "desktop" | "mobile";
};

/**
 * 判断页脚社交项是否配置了二维码。
 * @param item 页脚社交图标配置项。
 */
function hasQrCode(item: SiteFooterSocialItem): item is SiteFooterQrSocialItem {
  return Boolean(item.qrCodeSrc);
}

type QrPopoverPosition = {
  itemId: string;
  arrowLeft: number;
  left: number;
  top: number;
};

const qrPopoverWidth = 104;
const qrPopoverViewportMargin = 8;
const qrPopoverArrowEdgeMargin = 12;
const qrPopoverVerticalGap = 8;

/**
 * 渲染页脚社交图标和二维码切换区域。
 * @param props 组件参数。
 * @param props.items 当前语言下的社交图标配置列表。
 * @param props.variant 当前图标组使用的桌面或移动端几何规格。
 */
export function FooterSocialLinks({
  items,
  variant = "desktop",
}: FooterSocialLinksProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const qrButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const [hoveredQrId, setHoveredQrId] = useState<string | null>(null);
  const [pinnedQrId, setPinnedQrId] = useState<string | null>(null);
  const [suppressedHoverQrId, setSuppressedHoverQrId] = useState<
    string | null
  >(null);
  const [qrPopoverPosition, setQrPopoverPosition] =
    useState<QrPopoverPosition | null>(null);
  const activeQrId = hoveredQrId ?? pinnedQrId;
  const activeQrItem = items.find(
    (item): item is SiteFooterQrSocialItem =>
      hasQrCode(item) && item.id === activeQrId,
  );

  const updateQrPopoverPosition = useCallback(() => {
    if (!activeQrId) return;

    const container = containerRef.current;
    const activeButton = qrButtonRefs.current.get(activeQrId);
    if (!container || !activeButton) return;

    const containerBounds = container.getBoundingClientRect();
    const buttonBounds = activeButton.getBoundingClientRect();
    const buttonCenter = buttonBounds.left + buttonBounds.width / 2;
    const preferredViewportLeft = buttonCenter - qrPopoverWidth / 2;
    const viewportWidth = document.documentElement.clientWidth;
    const maximumViewportLeft = Math.max(
      qrPopoverViewportMargin,
      viewportWidth - qrPopoverWidth - qrPopoverViewportMargin,
    );
    const clampedViewportLeft = Math.min(
      Math.max(preferredViewportLeft, qrPopoverViewportMargin),
      maximumViewportLeft,
    );
    const arrowLeft = Math.min(
      Math.max(
        buttonCenter - clampedViewportLeft,
        qrPopoverArrowEdgeMargin,
      ),
      qrPopoverWidth - qrPopoverArrowEdgeMargin,
    );

    setQrPopoverPosition({
      itemId: activeQrId,
      arrowLeft,
      left: clampedViewportLeft - containerBounds.left,
      top:
        buttonBounds.bottom - containerBounds.top + qrPopoverVerticalGap,
    });
  }, [activeQrId]);

  useEffect(() => {
    if (!activeQrId) return;

    updateQrPopoverPosition();
    window.addEventListener("resize", updateQrPopoverPosition);
    window.addEventListener("scroll", updateQrPopoverPosition, true);

    return () => {
      window.removeEventListener("resize", updateQrPopoverPosition);
      window.removeEventListener("scroll", updateQrPopoverPosition, true);
    };
  }, [activeQrId, updateQrPopoverPosition]);

  const registerQrButton = useCallback(
    (itemId: string, button: HTMLButtonElement | null) => {
      if (button) {
        qrButtonRefs.current.set(itemId, button);
        return;
      }

      qrButtonRefs.current.delete(itemId);
    },
    [],
  );

  const handleQrButtonClick = useCallback(
    (itemId: string) => {
      if (pinnedQrId === itemId) {
        setPinnedQrId(null);
        setHoveredQrId(null);
        setSuppressedHoverQrId(itemId);
        return;
      }

      setPinnedQrId(itemId);
      setSuppressedHoverQrId(null);
    },
    [pinnedQrId],
  );

  if (items.length === 0) return null;

  return (
    <div
      className={cn(
        variant === "mobile"
          ? "flex h-full items-center justify-center"
          : "mt-4",
      )}
    >
      <div
        className="relative grid max-w-full items-start overflow-visible"
        ref={containerRef}
        style={{
          columnGap: variant === "mobile" ? "40.792333px" : "1.25rem",
          gridTemplateColumns: `repeat(${items.length}, max-content)`,
        }}
      >
        {items.map((item) => {
          const icon = (
            <FireflyIcon
              aria-hidden="true"
              className="h-5 w-auto"
              name={item.icon}
            />
          );
          const controlClassName = cn(
            "inline-flex items-center justify-center text-[#808080] transition hover:text-[#ff6600] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff6600]",
            variant === "mobile" ? "h-[21px]" : "h-8 min-w-5",
            hasQrCode(item) ? "cursor-pointer" : "",
            hasQrCode(item) && item.id === activeQrItem?.id
              ? "text-[#666666]"
              : "",
          );

          if (hasQrCode(item)) {
            const isPinnedQr = item.id === pinnedQrId;

            return (
              <button
                aria-controls={`footer-social-qr-${item.id}`}
                aria-expanded={item.id === activeQrId}
                aria-label={item.label}
                aria-pressed={isPinnedQr}
                className={controlClassName}
                key={item.id}
                onBlur={() => {
                  setHoveredQrId(null);
                  setSuppressedHoverQrId(null);
                }}
                onClick={() => handleQrButtonClick(item.id)}
                onFocus={() => {
                  if (suppressedHoverQrId !== item.id) {
                    setHoveredQrId(item.id);
                  }
                }}
                onMouseEnter={() => {
                  if (suppressedHoverQrId !== item.id) {
                    setHoveredQrId(item.id);
                  }
                }}
                onMouseLeave={() => {
                  setHoveredQrId(null);
                  setSuppressedHoverQrId(null);
                }}
                ref={(button) => registerQrButton(item.id, button)}
                type="button"
              >
                {icon}
              </button>
            );
          }

          return (
            <a
              aria-label={item.label}
              className={controlClassName}
              href={item.href}
              key={item.id}
              rel="noopener noreferrer"
              target="_blank"
            >
              {icon}
            </a>
          );
        })}

        {activeQrItem ? (
          <div
            className={cn(
              "absolute z-10 transition-opacity duration-150",
              qrPopoverPosition?.itemId === activeQrItem.id
                ? "opacity-100"
                : "opacity-0",
            )}
            id={`footer-social-qr-${activeQrItem.id}`}
            style={{
              left: qrPopoverPosition?.left ?? 0,
              top: qrPopoverPosition?.top ?? 0,
            }}
          >
            <div
              className="relative inline-flex rounded-[6px] bg-[#d9d9d9] p-[7px]"
              style={{
                "--footer-qr-arrow-left": `${qrPopoverPosition?.arrowLeft ?? qrPopoverWidth / 2}px`,
              } as CSSProperties}
            >
              <span
                aria-hidden="true"
                className="absolute top-[-6px] -translate-x-1/2 border-x-[6px] border-b-[6px] border-x-transparent border-b-[#d9d9d9]"
                style={{ left: "var(--footer-qr-arrow-left)" }}
              />
              <Image
                alt={activeQrItem.qrCodeAlt}
                className="rounded-[2px]"
                height={91}
                src={activeQrItem.qrCodeSrc}
                width={90}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
